import { httpsCallable } from "firebase/functions";
import { auth, functions } from "@/firebase/authorityClient";
import {
  isApiKeyAuthorityCommandsFeatureEnabled,
  isApiKeyAuthorityReadsFeatureEnabled,
} from "@/config/runtimeConfig";
import { withUiDeadline } from "@/utils/uiDeadline";

const ORG_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/;
const OPERATION_ID_PATTERN = /^[A-Za-z0-9_-]{16,128}$/;
const SHA256_PATTERN = /^[a-f0-9]{64}$/;
const FINGERPRINT_PATTERN = /^[a-f0-9]{12}$/;
const TOKEN_PATTERN = /^uk1\.([a-f0-9]{64})\.([A-Za-z0-9_-]{43})$/;
const CONTROL_CHARACTERS =
  /[\u0000-\u001f\u007f-\u009f\u061c\u200e\u200f\u202a-\u202e\u2066-\u2069]/;
const API_KEY_SCOPES = Object.freeze([
  "federated-organisation:provision",
  "identity:deprovision",
  "identity:provision",
  "software-activation:authorize",
  "usage:write",
]);
const API_KEY_AUDIENCES = Object.freeze([
  "federated-software-authorization",
  "federation-provisioning",
  "partner-provisioning",
  "metering",
]);
const API_KEY_STATUSES = new Set(["active", "revoked"]);
const MAX_API_KEYS = 25;
const MIN_EXPIRY_DAYS = 1;
const MAX_EXPIRY_DAYS = 365;
const MIN_QUOTA_WINDOW_SECONDS = 60;
const MAX_QUOTA_WINDOW_SECONDS = 86_400;
const MIN_QUOTA_REQUESTS = 1;
const MAX_QUOTA_REQUESTS = 100_000;
const KEY_KEYS = Object.freeze([
  "keyId",
  "name",
  "orgId",
  "scopes",
  "audiences",
  "quota",
  "status",
  "revision",
  "fingerprint",
  "createdAt",
  "expiresAt",
  "revokedAt",
]);
const CREATE_INTENT_KEYS = Object.freeze([
  "operationId",
  "expectedRevision",
  "orgId",
  "name",
  "scopes",
  "audiences",
  "expiresInDays",
  "quota",
]);
const REVISION_INTENT_KEYS = Object.freeze([
  "operationId",
  "expectedRevision",
  "orgId",
  "keyId",
]);

export class ApiKeyAuthorityServiceError extends Error {
  constructor(code = "unknown", message = "API key management is unavailable.") {
    super(message);
    this.name = "ApiKeyAuthorityServiceError";
    this.code = code;
  }
}

function fail(code, message = "API key management is unavailable.") {
  throw new ApiKeyAuthorityServiceError(code, message);
}

function isPlainRecord(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function hasExactKeys(value, expectedKeys) {
  if (!isPlainRecord(value)) return false;
  const ownKeys = Reflect.ownKeys(value);
  const descriptors = Object.getOwnPropertyDescriptors(value);
  const actual = ownKeys
    .filter((key) => typeof key === "string")
    .sort();
  const expected = [...expectedKeys].sort();
  return (
    ownKeys.every(
      (key) =>
        typeof key === "string" &&
        descriptors[key]?.enumerable === true &&
        Object.prototype.hasOwnProperty.call(descriptors[key], "value"),
    ) &&
    actual.length === expected.length &&
    actual.every((key, index) => key === expected[index])
  );
}

function requireExactKeys(value, expectedKeys) {
  if (!hasExactKeys(value, expectedKeys)) {
    fail("invalid-argument", "The API key request is invalid.");
  }
  return value;
}

function normalizeOrgId(value) {
  if (typeof value !== "string" || !ORG_ID_PATTERN.test(value)) {
    fail("invalid-argument", "The organisation is invalid.");
  }
  return value;
}

function normalizeOperationId(value) {
  if (typeof value !== "string" || !OPERATION_ID_PATTERN.test(value)) {
    fail("invalid-argument", "The API key operation is invalid.");
  }
  return value;
}

function normalizeKeyId(value) {
  if (typeof value !== "string" || !SHA256_PATTERN.test(value)) {
    fail("invalid-argument", "The API key identifier is invalid.");
  }
  return value;
}

function normalizeRevision(value, { creation = false } = {}) {
  if (
    !Number.isSafeInteger(value) ||
    value < 0 ||
    (creation ? value !== 0 : value < 1)
  ) {
    fail("invalid-argument", "The API key revision is invalid.");
  }
  return value;
}

function normalizeName(value) {
  const name = typeof value === "string" ? value.trim() : "";
  if (
    name.length < 1 ||
    name.length > 80 ||
    CONTROL_CHARACTERS.test(name)
  ) {
    fail("invalid-argument", "The API key name is invalid.");
  }
  return name;
}

function normalizeEnumSet(value, allowlist) {
  if (
    !Array.isArray(value) ||
    value.length < 1 ||
    value.length > allowlist.length
  ) {
    fail("invalid-argument", "The API key permissions are invalid.");
  }
  const normalized = [...value].sort();
  if (
    new Set(normalized).size !== normalized.length ||
    normalized.some((entry) => !allowlist.includes(entry))
  ) {
    fail("invalid-argument", "The API key permissions are invalid.");
  }
  return Object.freeze(normalized);
}

function normalizeQuota(value, code = "invalid-argument") {
  if (
    !hasExactKeys(value, ["windowSeconds", "maxRequests"]) ||
    !Number.isSafeInteger(value.windowSeconds) ||
    value.windowSeconds < MIN_QUOTA_WINDOW_SECONDS ||
    value.windowSeconds > MAX_QUOTA_WINDOW_SECONDS ||
    !Number.isSafeInteger(value.maxRequests) ||
    value.maxRequests < MIN_QUOTA_REQUESTS ||
    value.maxRequests > MAX_QUOTA_REQUESTS
  ) {
    fail(code, code === "invalid-response"
      ? "The API key response was invalid."
      : "The API key quota is invalid.");
  }
  return Object.freeze({
    windowSeconds: value.windowSeconds,
    maxRequests: value.maxRequests,
  });
}

export function createApiKeyAuthorityOperationId(
  cryptoProvider = globalThis.crypto,
) {
  if (typeof cryptoProvider?.randomUUID === "function") {
    return cryptoProvider.randomUUID().replaceAll("-", "_");
  }
  if (typeof cryptoProvider?.getRandomValues === "function") {
    const bytes = cryptoProvider.getRandomValues(new Uint8Array(24));
    return `api_key_${Array.from(bytes, (byte) =>
      byte.toString(16).padStart(2, "0"),
    ).join("")}`;
  }
  fail(
    "failed-precondition",
    "Secure API key operations are unavailable.",
  );
}

export function normalizeCreateAuthorityApiKeyIntent(value) {
  const intent = requireExactKeys(value, CREATE_INTENT_KEYS);
  if (
    !Number.isSafeInteger(intent.expiresInDays) ||
    intent.expiresInDays < MIN_EXPIRY_DAYS ||
    intent.expiresInDays > MAX_EXPIRY_DAYS
  ) {
    fail("invalid-argument", "The API key expiry is invalid.");
  }
  return Object.freeze({
    schemaVersion: 1,
    operationId: normalizeOperationId(intent.operationId),
    expectedRevision: normalizeRevision(intent.expectedRevision, {
      creation: true,
    }),
    orgId: normalizeOrgId(intent.orgId),
    name: normalizeName(intent.name),
    scopes: normalizeEnumSet(intent.scopes, API_KEY_SCOPES),
    audiences: normalizeEnumSet(intent.audiences, API_KEY_AUDIENCES),
    expiresInDays: intent.expiresInDays,
    quota: normalizeQuota(intent.quota),
  });
}

function normalizeRevisionIntent(value) {
  const intent = requireExactKeys(value, REVISION_INTENT_KEYS);
  return Object.freeze({
    schemaVersion: 1,
    operationId: normalizeOperationId(intent.operationId),
    expectedRevision: normalizeRevision(intent.expectedRevision),
    orgId: normalizeOrgId(intent.orgId),
    keyId: normalizeKeyId(intent.keyId),
  });
}

function normalizeTimestamp(value) {
  if (!Number.isSafeInteger(value) || value < 0) {
    fail("invalid-response", "The API key response was invalid.");
  }
  return value;
}

export function normalizeAuthorityApiKey(value, expectedOrgId = null) {
  if (!hasExactKeys(value, KEY_KEYS)) {
    fail("invalid-response", "The API key response was invalid.");
  }
  const keyId = normalizeResponseKeyId(value.keyId);
  const orgId = normalizeResponseOrgId(value.orgId);
  const name = normalizeResponseName(value.name);
  const scopes = normalizeResponseEnumSet(value.scopes, API_KEY_SCOPES);
  const audiences = normalizeResponseEnumSet(
    value.audiences,
    API_KEY_AUDIENCES,
  );
  const status = value.status;
  const createdAt = normalizeTimestamp(value.createdAt);
  const expiresAt = normalizeTimestamp(value.expiresAt);
  const revokedAt = value.revokedAt;
  if (
    (expectedOrgId !== null && orgId !== expectedOrgId) ||
    !API_KEY_STATUSES.has(status) ||
    !Number.isSafeInteger(value.revision) ||
    value.revision < 1 ||
    typeof value.fingerprint !== "string" ||
    !FINGERPRINT_PATTERN.test(value.fingerprint) ||
    expiresAt <= createdAt ||
    (status === "active" && revokedAt !== null) ||
    (status === "revoked" &&
      (!Number.isSafeInteger(revokedAt) || revokedAt < createdAt))
  ) {
    fail("invalid-response", "The API key response was invalid.");
  }
  return Object.freeze({
    keyId,
    name,
    orgId,
    scopes,
    audiences,
    quota: normalizeQuota(value.quota, "invalid-response"),
    status,
    revision: value.revision,
    fingerprint: value.fingerprint,
    createdAt,
    expiresAt,
    revokedAt,
  });
}

function normalizeResponseKeyId(value) {
  if (typeof value !== "string" || !SHA256_PATTERN.test(value)) {
    fail("invalid-response", "The API key response was invalid.");
  }
  return value;
}

function normalizeResponseOrgId(value) {
  if (typeof value !== "string" || !ORG_ID_PATTERN.test(value)) {
    fail("invalid-response", "The API key response was invalid.");
  }
  return value;
}

function normalizeResponseName(value) {
  if (
    typeof value !== "string" ||
    value !== value.trim() ||
    value.length < 1 ||
    value.length > 80 ||
    CONTROL_CHARACTERS.test(value)
  ) {
    fail("invalid-response", "The API key response was invalid.");
  }
  return value;
}

function normalizeResponseEnumSet(value, allowlist) {
  if (
    !Array.isArray(value) ||
    value.length < 1 ||
    value.length > allowlist.length ||
    new Set(value).size !== value.length ||
    value.some((entry) => !allowlist.includes(entry)) ||
    value.some((entry, index) => index > 0 && value[index - 1] >= entry)
  ) {
    fail("invalid-response", "The API key response was invalid.");
  }
  return Object.freeze([...value]);
}

function validateListResponse(value, orgId) {
  if (
    !hasExactKeys(value, ["schemaVersion", "keys"]) ||
    value.schemaVersion !== 1 ||
    !Array.isArray(value.keys) ||
    value.keys.length > MAX_API_KEYS
  ) {
    fail("invalid-response", "The API key response was invalid.");
  }
  const keys = value.keys.map((key) => normalizeAuthorityApiKey(key, orgId));
  if (new Set(keys.map(({ keyId }) => keyId)).size !== keys.length) {
    fail("invalid-response", "The API key response was invalid.");
  }
  return Object.freeze({ schemaVersion: 1, keys: Object.freeze(keys) });
}

function validateIssuanceResponse(value, { orgId, outcome }) {
  if (
    !hasExactKeys(value, ["schemaVersion", "outcome", "key", "token"]) ||
    value.schemaVersion !== 1 ||
    ![outcome, "replayed"].includes(value.outcome) ||
    typeof value.token !== "string"
  ) {
    fail("invalid-response", "The API key response was invalid.");
  }
  const key = normalizeAuthorityApiKey(value.key, orgId);
  const tokenMatch = TOKEN_PATTERN.exec(value.token);
  if (key.status !== "active" || !tokenMatch || tokenMatch[1] !== key.keyId) {
    fail("invalid-response", "The API key response was invalid.");
  }
  // The bearer token deliberately exists only in this immediate command
  // result. The service has no cache and key metadata never accepts it.
  return Object.freeze({
    schemaVersion: 1,
    outcome: value.outcome,
    key,
    token: value.token,
  });
}

function validateRevocationResponse(value, request) {
  if (
    !hasExactKeys(value, ["schemaVersion", "outcome", "key"]) ||
    value.schemaVersion !== 1 ||
    !["revoked", "unchanged", "replayed"].includes(value.outcome)
  ) {
    fail("invalid-response", "The API key response was invalid.");
  }
  const key = normalizeAuthorityApiKey(value.key, request.orgId);
  if (key.keyId !== request.keyId || key.status !== "revoked") {
    fail("invalid-response", "The API key response was invalid.");
  }
  return Object.freeze({ schemaVersion: 1, outcome: value.outcome, key });
}

function normalizeServiceError(error) {
  if (error instanceof ApiKeyAuthorityServiceError) return error;
  const messages = {
    aborted: "This API key changed elsewhere. Load the latest version and try again.",
    "deadline-exceeded": "API key management did not respond in time.",
    "failed-precondition": "API key management is temporarily unavailable.",
    "invalid-argument": "The API key request was invalid.",
    "not-found": "The API key is unavailable.",
    "permission-denied": "You do not have permission to manage these API keys.",
    "resource-exhausted": "The organisation API key limit was reached.",
    "ui-deadline": "API key management did not respond in time.",
    unauthenticated: "Sign in again to manage API keys.",
    unavailable: "API key management is temporarily unavailable.",
  };
  const candidateCode = String(error?.code || "unknown").split("/").pop();
  const code = Object.hasOwn(messages, candidateCode)
    ? candidateCode
    : "unknown";
  return new ApiKeyAuthorityServiceError(
    code,
    messages[code] || "The API key operation could not be completed.",
  );
}

function authenticatedUid(authInstance) {
  const uid = authInstance?.currentUser?.uid;
  if (typeof uid !== "string" || uid.length < 1 || uid.length > 128) {
    fail("unauthenticated", "Sign in again to manage API keys.");
  }
  return uid;
}

export function createApiKeyAuthorityService({
  authInstance = auth,
  functionsInstance = functions,
  callableFactory = httpsCallable,
  readsEnabled = isApiKeyAuthorityReadsFeatureEnabled,
  commandsEnabled = isApiKeyAuthorityCommandsFeatureEnabled,
} = {}) {
  let listCallable;
  let createCallable;
  let rotateCallable;
  let revokeCallable;

  const invoke = async (callable, request, phase) => {
    const expectedUid = authenticatedUid(authInstance);
    try {
      const response = await withUiDeadline(callable(request), { phase });
      if (authInstance?.currentUser?.uid !== expectedUid) {
        fail(
          "stale-auth-context",
          "The signed-in account changed during the API key operation.",
        );
      }
      return response;
    } catch (error) {
      throw normalizeServiceError(error);
    }
  };

  return Object.freeze({
    async listAuthorityApiKeys(orgIdValue) {
      if (!readsEnabled) {
        fail("feature-disabled", "API key reads are not enabled.");
      }
      const orgId = normalizeOrgId(orgIdValue);
      if (!listCallable) {
        listCallable = callableFactory(functionsInstance, "listAuthorityApiKeys");
      }
      const response = await invoke(
        listCallable,
        { schemaVersion: 1, orgId },
        "API keys",
      );
      return validateListResponse(response?.data, orgId);
    },

    async createAuthorityApiKey(intentValue) {
      if (!commandsEnabled) {
        fail("feature-disabled", "API key commands are not enabled.");
      }
      const request = normalizeCreateAuthorityApiKeyIntent(intentValue);
      if (!createCallable) {
        createCallable = callableFactory(functionsInstance, "createAuthorityApiKey");
      }
      const response = await invoke(
        createCallable,
        request,
        "creating API key",
      );
      return validateIssuanceResponse(response?.data, {
        orgId: request.orgId,
        outcome: "created",
      });
    },

    async rotateAuthorityApiKey(intentValue) {
      if (!commandsEnabled) {
        fail("feature-disabled", "API key commands are not enabled.");
      }
      const request = normalizeRevisionIntent(intentValue);
      if (!rotateCallable) {
        rotateCallable = callableFactory(functionsInstance, "rotateAuthorityApiKey");
      }
      const response = await invoke(
        rotateCallable,
        request,
        "rotating API key",
      );
      return validateIssuanceResponse(response?.data, {
        orgId: request.orgId,
        outcome: "rotated",
      });
    },

    async revokeAuthorityApiKey(intentValue) {
      if (!commandsEnabled) {
        fail("feature-disabled", "API key commands are not enabled.");
      }
      const request = normalizeRevisionIntent(intentValue);
      if (!revokeCallable) {
        revokeCallable = callableFactory(functionsInstance, "revokeAuthorityApiKey");
      }
      const response = await invoke(
        revokeCallable,
        request,
        "revoking API key",
      );
      return validateRevocationResponse(response?.data, request);
    },
  });
}

export const apiKeyAuthorityService = createApiKeyAuthorityService();
